import {
  PhoneNumber,
  PhoneNumberFormat,
  PhoneNumberType,
  PhoneNumberUtil,
} from 'google-libphonenumber';
import { z } from 'zod';

const PhoneUtil = PhoneNumberUtil.getInstance();

const getValidationResultName = (reason: PhoneNumberUtil.ValidationResult) =>
  Object.entries(PhoneNumberUtil.ValidationResult).find(([, value]) => value === reason)?.[0];

export const createZodPhoneNumberError = (message: string) => ({
  code: z.ZodIssueCode.custom,
  message,
  path: [],
});

/**
 *  We must first parse the phone number to pass into the validator
 *
 * @param value input
 * @param countries countries to try and parse with
 * @returns parse result
 *
 * @throws {Error} throws when input isn't valid
 */
export const parsePhoneNumber = (value: string, [...countries] = [] as string[]): PhoneNumber => {
  const country = countries.shift();
  try {
    return PhoneUtil.parse(value, country);
  } catch (error) {
    if (countries.length > 0) {
      return parsePhoneNumber(value, countries);
    }

    throw error;
  }
};

const createPhoneNumberValidator = (options?: { mobileOnly?: boolean }) =>
  z
    .string()
    .transform((inputString, ctx) => {
      let parsed: PhoneNumber | null = null;
      try {
        parsed = parsePhoneNumber(inputString, ['US', 'BE']); // default countries used in joiPN
      } catch (error) {
        ctx.addIssue(createZodPhoneNumberError((error as Error).message));
        return inputString;
      }

      const isValid = PhoneUtil.isValidNumber(parsed);
      if (!isValid) {
        const errorReason = PhoneUtil.isPossibleNumberWithReason(parsed);
        ctx.addIssue(
          createZodPhoneNumberError(
            `Invalid phone number: ${getValidationResultName(errorReason)}`,
          ),
        );
        return inputString;
      }

      if (options?.mobileOnly === true) {
        const numberType = PhoneUtil.getNumberType(parsed);
        const isMobile =
          numberType === PhoneNumberType.MOBILE ||
          numberType === PhoneNumberType.FIXED_LINE_OR_MOBILE;
        if (!isMobile) {
          ctx.addIssue(createZodPhoneNumberError('Phone number must be a mobile number'));
          return inputString;
        }
      }

      return PhoneUtil.format(parsed, PhoneNumberFormat.E164);
    })
    .meta({
      override: { type: 'string' },
    });

export const ZodPhoneNumber = createPhoneNumberValidator();

export const ZodMobilePhoneNumber = createPhoneNumberValidator({ mobileOnly: true });

export const UNSUPPORTED_PHONE_NUMBER_PREFIXED = [
  '+1',
  '+7',
  '+20',
  '+55',
  '+61',
  '+62',
  '+63',
  '+65',
  '+81',
  '+82',
  '+84',
  '+86',
  '+90',
  '+91',
  '+254',
  '+966',
  '+971',
];

export function isUnsupportedPhoneNumber(phoneNumber: string): boolean {
  return UNSUPPORTED_PHONE_NUMBER_PREFIXED.some((prefix) => phoneNumber.startsWith(prefix));
}
